import { ColDef, GetRowIdParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { getDefaultRowIndexColumnConfig, getRowStyle } from 'src/utils/grid/gridStyleUtils';
import { defaultEQM1011_R01Data, EQM1011_R01Dto } from './Dto_EQM1011_R01';
import { useEQM1011_R01Store } from './Store_EQM1011_R01';

const COLUMN_STATE_KEY = 'EQM1011_R01-grid-column-state';
const EMPTY_SELECTED_ROWS: string[] = [];
const HEADER_NAMES: Record<string, string> = {
    rowIndex: '',
    compCd: '사업장',
    compNm: '사업장명',
    plcId: 'PLC 번호',
    plcName: 'PLC명',
    tagId: '데이터종류',
    tagName: '데이터명',
    valueStr: '수집데이터값',
    readAt: '수집일시',
};

export const Table_EQM1011_R01 = () => {
    const gridRef = useRef<AgGridReact<EQM1011_R01Dto>>(null);
    const {
        EQM1011_R01List,
        columnState,
        setColumnState,
        scrollTop,
        setScrollTop,
    } = useEQM1011_R01Store();
    const { compCdList } = useCommonCodeStore();
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const { createRowClassRules, paddedGridData } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: EQM1011_R01List,
        defaultGridData: defaultEQM1011_R01Data,
        selectedRow: EMPTY_SELECTED_ROWS,
    });

    // 행 ID — 실제 이력 행은 이력 일련번호(histId), 빈 채움 행은 valueIndex로 구분 (옛 plcstatuslog id 대체)
    const getEQM1011_R01RowId = useCallback((params: GetRowIdParams<EQM1011_R01Dto>) => (
        params.data.histId > 0
            ? String(params.data.histId)
            : `placeholder-${params.data.valueIndex}`
    ), []);

    const companyMap = useMemo(
        () => new Map(compCdList.map((item) => [item.compCd, item.compNm])),
        [compCdList],
    );
    const getCellStyle = useCallback(
        (params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params),
        [],
    );

    const baseColumnDefs: ColDef<EQM1011_R01Dto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        {
            headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'compCd'),
        },
        {
            headerName: '사업장명', colId: 'compNm', field: 'compNm', width: 150,
            headerClass: aggridHeaderClass,
            valueGetter: (params) => companyMap.get(params.data?.compCd ?? '')
                || params.data?.compNm
                || '',
            cellStyle: (params) => getCellStyle(params, 'compNm'),
        },
        // 6차 요청: plc_tag_hist 조회로 바뀌어 필드명이 DB 컬럼명을 따른다. PLC 번호·데이터종류는 문자열(예: PLC01, PLC01_T130)이라 숫자 정렬 타입을 쓰지 않는다
        {
            headerName: 'PLC 번호', colId: 'plcId', field: 'plcId', width: 90,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'plcId'),
        },
        {
            headerName: 'PLC명', colId: 'plcName', field: 'plcName', width: 120,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'plcName'),
        },
        {
            headerName: '데이터종류', colId: 'tagId', field: 'tagId', width: 130,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'tagId'),
        },
        {
            headerName: '데이터명', colId: 'tagName', field: 'tagName', width: 140,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'tagName'),
        },
        {
            headerName: '수집데이터값', colId: 'valueStr', field: 'valueStr', width: 120,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'valueStr'),
        },
        {
            headerName: '수집일시', colId: 'readAt', field: 'readAt', width: 160,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'readAt'),
        },
    ], [companyMap, getCellStyle]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
        [baseColumnDefs],
    );
    const columnDefs = useMemo(() => {
        if (columnState === null) return baseColumnDefs;
        // 예전에 저장된 컬럼 배치에 옛 colId(plcid, plcaddressid, plcAddressName, value, createdatetime)가 남아 있으면
        // 공통 함수가 이름 없는 빈 컬럼으로 되살리므로, 현재 컬럼 정의에 있는 컬럼의 배치만 복원한다.
        const currentColIds = baseColumnDefs.map(column => ('colId' in column ? column.colId : undefined));
        return GridCustomColumnDefs<EQM1011_R01Dto>({
            baseColumnDefs,
            columnState: columnState.filter(state => currentColIds.includes(state.colId)),
            headerNames: HEADER_NAMES,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [baseColumnDefs, columnState]);
    const pinnedBottomRow = useMemo(() => ([{
        rowIndex: '',
        compCd: `${EQM1011_R01List.length.toLocaleString()}건`,
        editable: false,
    }]), [EQM1011_R01List]);

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            <GridContextWrapper<EQM1011_R01Dto>
                gridRef={gridRef}
                paddedGridData={paddedGridData}
                enableCellCalculation
                initSelected={() => {}}
                defaultRowData={defaultEQM1011_R01Data}
                baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}
                menuItems={undefined}
            >
                <Grid_Touch<EQM1011_R01Dto>
                    ref={gridRef}
                    rowData={paddedGridData}
                    columnDefs={columnDefs}
                    getRowId={getEQM1011_R01RowId}
                    onCellKeyDown={undefined}
                    pinnedBottomRowData={pinnedBottomRow}
                    rowClassRules={createRowClassRules<EQM1011_R01Dto>({
                        idKey: 'histId', // 행 식별값: 이력 일련번호 (옛 id 대체)
                        selectedList: EMPTY_SELECTED_ROWS,
                        deletedList: [],
                    })}
                    suppressRowClickSelection={true}
                    animateRows={true}
                    enableCellTextSelection={true}
                    ensureDomOrder={false}
                    getRowStyle={getRowStyle}
                    setHoveredRowIndex={setHoveredRowIndex}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={setColumnState}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop}
                />
            </GridContextWrapper>
        </div>
    );
};
